import {
    UploadOutlined,
    FileExcelOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    WarningOutlined,
    ReloadOutlined,
    InboxOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import {
    Card,
    Button,
    Modal,
    Upload,
    Progress,
    Row,
    Col,
    Statistic,
    Alert,
    List,
    Tag,
    Space,
    Typography,
    Divider,
    Result
} from 'antd';
import { useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../../variables/api/axiosInstance';

const { Dragger } = Upload;
const { Text, Title } = Typography;

const ClesRepartitionImport = ({ visible, onClose, onImportSuccess }) => {
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [importResult, setImportResult] = useState(null);

    const resetState = () => {
        setFileList([]);
        setUploading(false);
        setUploadProgress(0);
        setImportResult(null);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleUpload = async () => {
        if (fileList.length === 0) {
            toast.warning("Veuillez sélectionner un fichier");
            return;
        }

        const formData = new FormData();
        formData.append('file', fileList[0]);

        setUploading(true);
        setUploadProgress(0);
        setImportResult(null);

        try {
            const response = await axiosInstance.post('/api/cle-repartition/migration', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percent);
                },
            });

            setImportResult(response.data);

            if (response.data.erreurs?.length > 0) {
                toast.warning("Import terminé avec des erreurs");
            } else {
                toast.success("Import terminé avec succès");
                if (onImportSuccess) {
                    onImportSuccess();
                }
            }
        } catch (error) {
            console.error('Erreur lors de l\'import:', error);
            toast.error(error.response?.data?.message || "Erreur lors de l'import du fichier");
            setImportResult({
                erreurs: [error.response?.data?.message || "Une erreur inattendue s'est produite"],
                totalLignes: 0,
                lignesImportees: 0,
                lignesMisesAJour: 0,
                lignesIgnorees: 0
            });
        } finally {
            setUploading(false);
        }
    };

    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: '.xlsx',
        fileList,
        beforeUpload: (file) => {
            const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                || file.name.endsWith('.xlsx');

            if (!isExcel) {
                toast.error('Seuls les fichiers Excel (.xlsx) sont acceptés');
                return Upload.LIST_IGNORE;
            }

            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                toast.error('Le fichier doit faire moins de 10MB');
                return Upload.LIST_IGNORE;
            }

            setFileList([file]);
            setImportResult(null);
            return false; // Empêcher l'upload automatique
        },
        onRemove: () => {
            setFileList([]);
            setImportResult(null);
        },
    };

    const renderImportResult = () => {
        if (!importResult) return null;

        const { totalLignes, lignesImportees, lignesMisesAJour, lignesIgnorees, erreurs, avertissements } = importResult;
        const hasErrors = erreurs?.length > 0;
        const hasWarnings = avertissements?.length > 0;

        return (
            <div style={{ marginTop: 24 }}>
                <Divider>Résultat de l'import</Divider>

                {/* Statistiques */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={12} sm={6}>
                        <Card size="small" style={{ textAlign: 'center', borderLeft: '4px solid #52c41a' }}>
                            <Statistic
                                title="Créées"
                                value={lignesImportees}
                                valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                                prefix={<CheckCircleOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small" style={{ textAlign: 'center', borderLeft: '4px solid #1890ff' }}>
                            <Statistic
                                title="Mises à jour"
                                value={lignesMisesAJour}
                                valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                                prefix={<ReloadOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small" style={{ textAlign: 'center', borderLeft: '4px solid #faad14' }}>
                            <Statistic
                                title="Ignorées"
                                value={lignesIgnorees}
                                valueStyle={{ color: '#faad14', fontSize: '24px' }}
                                prefix={<WarningOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small" style={{ textAlign: 'center', borderLeft: '4px solid #722ed1' }}>
                            <Statistic
                                title="Total"
                                value={totalLignes}
                                valueStyle={{ color: '#722ed1', fontSize: '24px' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Message de succès */}
                {!hasErrors && (
                    <Result
                        status="success"
                        title="Import réussi !"
                        subTitle={`${lignesImportees} clé(s) créée(s) et ${lignesMisesAJour} clé(s) mise(s) à jour`}
                        style={{ padding: '24px 0' }}
                    />
                )}

                {/* Erreurs */}
                {hasErrors && (
                    <Alert
                        message="Erreurs rencontrées"
                        description={
                            <List
                                size="small"
                                dataSource={erreurs.slice(0, 10)}
                                renderItem={(item, index) => (
                                    <List.Item style={{ padding: '4px 0' }}>
                                        <Text type="danger">
                                            <CloseCircleOutlined style={{ marginRight: 8 }} />
                                            {item}
                                        </Text>
                                    </List.Item>
                                )}
                                footer={erreurs.length > 10 && (
                                    <Text type="secondary">
                                        ... et {erreurs.length - 10} autre(s) erreur(s)
                                    </Text>
                                )}
                            />
                        }
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {/* Avertissements */}
                {hasWarnings && (
                    <Alert
                        message="Avertissements"
                        description={
                            <List
                                size="small"
                                dataSource={avertissements.slice(0, 5)}
                                renderItem={(item) => (
                                    <List.Item style={{ padding: '4px 0' }}>
                                        <Text type="warning">
                                            <WarningOutlined style={{ marginRight: 8 }} />
                                            {item}
                                        </Text>
                                    </List.Item>
                                )}
                                footer={avertissements.length > 5 && (
                                    <Text type="secondary">
                                        ... et {avertissements.length - 5} autre(s) avertissement(s)
                                    </Text>
                                )}
                            />
                        }
                        type="warning"
                        showIcon
                    />
                )}
            </div>
        );
    };

    return (
        <Modal
            title={
                <span>
                    <UploadOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                    Import des Clés de Répartition
                </span>
            }
            open={visible}
            onCancel={handleClose}
            width={800}
            footer={
                <Space>
                    <Button onClick={handleClose}>
                        Fermer
                    </Button>
                    {!importResult && (
                        <Button
                            type="primary"
                            onClick={handleUpload}
                            disabled={fileList.length === 0}
                            loading={uploading}
                            icon={<UploadOutlined />}
                            style={{ background: '#722ed1', borderColor: '#722ed1' }}
                        >
                            {uploading ? 'Import en cours...' : 'Importer'}
                        </Button>
                    )}
                    {importResult && (
                        <Button
                            type="primary"
                            onClick={resetState}
                            icon={<ReloadOutlined />}
                        >
                            Nouvel import
                        </Button>
                    )}
                </Space>
            }
        >
            {/* Instructions */}
            <Alert
                message="Format du fichier attendu"
                description={
                    <div>
                        <p>Le fichier Excel doit contenir les clés de répartition avec les colonnes suivantes :</p>
                        <ul style={{ marginBottom: 0 }}>
                            <li><strong>TPV / MOTO</strong> : Structure par année (2009-2025) avec taux par compagnie</li>
                            <li><strong>VT / GUM</strong> : Structure par date avec colonnes par compagnie</li>
                        </ul>
                        <p style={{ marginTop: 8, marginBottom: 0 }}>
                            <Text type="secondary">
                                Les clés existantes seront automatiquement mises à jour si elles correspondent
                                à la même combinaison catégorie/compagnie/exercice.
                            </Text>
                        </p>
                    </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            {/* Zone de dépôt */}
            {!importResult && (
                <>
                    <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined style={{ color: '#722ed1', fontSize: 48 }} />
                        </p>
                        <p className="ant-upload-text">
                            Cliquez ou glissez-déposez votre fichier Excel ici
                        </p>
                        <p className="ant-upload-hint">
                            Seuls les fichiers .xlsx sont acceptés (max 10MB)
                        </p>
                    </Dragger>

                    {/* Fichier sélectionné */}
                    {fileList.length > 0 && (
                        <Card size="small" style={{ marginTop: 16, background: '#f6ffed', borderColor: '#b7eb8f' }}>
                            <Space>
                                <FileExcelOutlined style={{ fontSize: 24, color: '#217346' }} />
                                <div>
                                    <Text strong>{fileList[0].name}</Text>
                                    <br />
                                    <Text type="secondary">
                                        {(fileList[0].size / 1024).toFixed(2)} KB
                                    </Text>
                                </div>
                                <Tag color="success">Prêt à importer</Tag>
                            </Space>
                        </Card>
                    )}

                    {/* Barre de progression */}
                    {uploading && (
                        <div style={{ marginTop: 24 }}>
                            <Progress
                                percent={uploadProgress}
                                status="active"
                                strokeColor={{
                                    '0%': '#722ed1',
                                    '100%': '#531dab',
                                }}
                            />
                            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
                                Traitement du fichier en cours...
                            </Text>
                        </div>
                    )}
                </>
            )}

            {/* Résultat de l'import */}
            {renderImportResult()}
        </Modal>
    );
};

export default ClesRepartitionImport;